import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { showSuccess, showError, showInfo } from '../utils/toast';

const VerifyEmail = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Kullanıcı yoksa login'e yönlendir
    if (!user) {
      navigate('/login');
      return;
    }

    // Kullanıcı zaten doğrulanmışsa ana sayfaya yönlendir
    if (user.isVerified) {
      navigate('/');
    }
  }, [user, navigate]);

  // Kod inputlarını handle et
  const handleCodeChange = (index, value) => {
    // Sadece rakam kabul et
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Otomatik olarak bir sonraki input'a geç
    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`).focus();
    }
  };

  // Backspace tuşuna basıldığında önceki input'a geç
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-${index - 1}`).focus();
    }
  };

  // Paste işlemini handle et
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.split('');

    while (newCode.length < 6) {
      newCode.push('');
    }

    setCode(newCode);

    // Son dolu input'a focus yap
    const lastFilledIndex = Math.min(pastedData.length, 5);
    document.getElementById(`code-${lastFilledIndex}`).focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const verificationCode = code.join('');

    if (verificationCode.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/auth/verify-email',
        { code: verificationCode },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess(true);
      showSuccess('Email başarıyla doğrulandı!');

      // Kullanıcı bilgilerini sunucudan yeniden çek
      try {
        const userResponse = await axios.get('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Güncel user bilgileriyle login yap
        login(userResponse.data, token);
      } catch (userErr) {
        console.error('User bilgileri çekilemedi:', userErr);
        // Fallback: Manuel olarak güncelle
        const updatedUser = { ...user, isVerified: true };
        login(updatedUser, token);
      }

      // 2 saniye sonra ana sayfaya yönlendir ve tam yenile
      setTimeout(() => {
        navigate('/');
        window.location.reload();
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Doğrulama başarısız. Lütfen tekrar deneyin.';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setResendMessage('');
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/auth/resend-code',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 15000 // 15 saniye timeout
        }
      );

      setResendMessage(response.data.message);
      showInfo('Yeni doğrulama kodu gönderildi!');
      setCode(['', '', '', '', '', '']);
      document.getElementById('code-0').focus();
    } catch (err) {
      let errorMsg;
      if (err.code === 'ECONNABORTED') {
        errorMsg = 'İstek zaman aşımına uğradı. Lütfen internet bağlantınızı kontrol edin.';
      } else {
        errorMsg = err.response?.data?.message || 'Kod gönderilemedi. Lütfen tekrar deneyin.';
      }
      setError(errorMsg);
      showError(errorMsg);
      console.error('Resend code error:', err);
    } finally {
      setResendLoading(false);
    }
  };

  // Admin kullanıcı kendini doğrulayabilir
  const handleAdminVerify = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/auth/admin/verify-self',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess(true);
      showSuccess('Admin doğrulama başarılı!');

      // Kullanıcı bilgilerini sunucudan yeniden çek
      try {
        const userResponse = await axios.get('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Güncel user bilgileriyle login yap
        login(userResponse.data, token);
      } catch (userErr) {
        console.error('User bilgileri çekilemedi:', userErr);
        // Fallback: Manuel olarak güncelle
        const updatedUser = { ...user, isVerified: true };
        login(updatedUser, token);
      }

      // 2 saniye sonra ana sayfaya yönlendir ve tam yenile
      setTimeout(() => {
        navigate('/');
        window.location.reload();
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Doğrulama başarısız.';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
            <FaCheckCircle className="mx-auto text-green-500 text-6xl mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Email Doğrulandı!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Email adresiniz başarıyla doğrulandı. Ana sayfaya yönlendiriliyorsunuz...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <FaEnvelope className="mx-auto text-primary-600 dark:text-primary-400 text-6xl mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Email Doğrulama
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {user?.email} adresine gönderilen 6 haneli kodu girin
          </p>
        </div>

        <form onSubmit={handleVerify} className="mt-8 space-y-6 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {resendMessage && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm">
              {resendMessage}
            </div>
          )}

          <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={loading || code.join('').length !== 6}
            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Doğrulanıyor...' : 'Doğrula'}
          </button>

          <div className="text-center space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Kod gelmedi mi?
            </p>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendLoading}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendLoading ? 'Gönderiliyor...' : 'Yeni Kod Gönder'}
            </button>

            {/* Admin kullanıcılar için özel doğrulama */}
            {user?.role === 'admin' && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Admin kullanıcısınız. Email almadan doğrulayabilirsiniz:
                </p>
                <button
                  type="button"
                  onClick={handleAdminVerify}
                  disabled={loading}
                  className="w-full py-2 px-4 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {loading ? 'Doğrulanıyor...' : 'Admin Olarak Doğrula'}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail;
